import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Container, Grid, CircularProgress } from '@mui/material';
import Fuse from 'fuse.js';
import ProductGrid from '../components/ProductGrid';
import ProductFilters from '../components/ProductFilters';
import ProductSortBar from '../components/ProductSortBar';
import SearchDescription from '../components/SearchDescription';
import { useProductsData } from '../services/dataApi';
import { useMeta } from '../contexts/MetaContext';

const ProductSearch = ({ searchTerm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { metaData } = useMeta();
  const [view, setView] = useState(metaData.search?.view || 'grid');
  const [sortBy, setSortBy] = useState(metaData.search?.sortBy || 'featured');
  const [page, setPage] = useState(1);
  const { data: productsData, loading } = useProductsData();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filters, setFilters] = useState({
    collections: [],
    brands: [],
    minPrice: '',
    maxPrice: '',
  });

  const fuse = useMemo(() => {
    if (productsData.length > 0) {
      return new Fuse(productsData, {
        keys: ['name', 'brand', 'category'],
        threshold: 0.3,
      });
    }
    return null;
  }, [productsData]);

  // Initialize filteredProducts when productsData loads
  useEffect(() => {
    if (productsData.length > 0) {
      setFilteredProducts(productsData);
    }
  }, [productsData]);

  // Parse URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const collections = searchParams.getAll('collection');
    const brands = searchParams.getAll('brand');
    
    setFilters(prev => ({
      ...prev,
      collections: collections,
      brands: brands,
    }));
  }, [location]);

  useEffect(() => {
    if (!productsData.length) return;

    let results = searchTerm && fuse ? fuse.search(searchTerm).map(result => result.item) : productsData;

    // Apply filters
    results = results.filter(product => {
      const matchesCollections = filters.collections.length === 0 || 
        filters.collections.some(c => product.category && product.category.includes(c));
      const matchesBrand = filters.brands.length === 0 || filters.brands.includes(product.brand);
      const matchesPrice = (filters.minPrice === '' || parseFloat(product.price) >= parseFloat(filters.minPrice)) &&
        (filters.maxPrice === '' || parseFloat(product.price) <= parseFloat(filters.maxPrice));
      return matchesCollections && matchesBrand && matchesPrice;
    });

    // Apply sorting
    results.sort((a, b) => {
      switch (sortBy) {
        case 'featured':
          return 0;
        case 'best-selling':
          return 0;
        case 'alphabetically-asc':
          return a.name.localeCompare(b.name);
        case 'alphabetically-desc':
          return b.name.localeCompare(a.name);
        case 'price-low-high':
          return parseFloat(a.price) - parseFloat(b.price);
        case 'price-high-low':
          return parseFloat(b.price) - parseFloat(a.price);
        case 'date-old-new':
          return new Date(a.created) - new Date(b.created);
        case 'date-new-old':
          return new Date(b.created) - new Date(a.created);
        default:
          return 0;
      }
    });

    setFilteredProducts(results);
    setPage(1);
  }, [searchTerm, filters, sortBy, fuse, productsData]);

  const handleFilterChange = (newFilters, filterType) => {
    setFilters(newFilters);
    
    // Update URL - we only do this for brand and collection changes
    if (filterType === 'brand' || filterType === 'collection') {
      const searchParams = new URLSearchParams();
      newFilters.collections.forEach(collection => {
        searchParams.append('collection', collection);
      });
      newFilters.brands.forEach(brand => {
        searchParams.append('brand', brand);
      });
      navigate(`/search?${searchParams.toString()}`);
    }
  };

  useEffect(() => {
    document.title = 'My Store - Search';
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Search Description Component */}
      <SearchDescription 
        collections={filters.collections} 
        brands={filters.brands} 
      />

      <Container maxWidth={false}>
        <ProductSortBar 
          view={view} 
          setView={setView} 
          sortBy={sortBy} 
          setSortBy={setSortBy} 
          productCount={filteredProducts.length}
        />
        <Grid container spacing={4}>
          <Grid item xs={12} md={2}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <ProductFilters 
                filters={filters} 
                onFilterChange={handleFilterChange} 
                filteredProducts={filteredProducts}
                allProducts={productsData}
              />
            )}
          </Grid>
          <Grid item xs={12} md={10}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <ProductGrid products={filteredProducts} view={view} page={page} setPage={setPage} />
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductSearch;